/**
 * SKU fields needed to call `POST /widget/limits/resolve`.
 * Compatible with a pre-quote `EstimateOption`.
 */
export interface FundingLimitsSku {
  corridorCode: string;
  fundingMethod: string;
  payoutMethod?: string;
  payerCode?: string | null;
  customerFee?: number | null;
}

export interface FundingMethodDetails {
  fee: number | null;
  minAmount: number | null;
  maxAmount: number | null;
}

export type FundingAmountBound = {
  minAmount: number | null;
  maxAmount: number | null;
};

/**
 * Inputs for a resolve SKU that does not depend on the priced option at the
 * current amount. Corridor comes from facet/route/sticky — never cheapest
 * `options[]`. Payout/payer are omitted on send/deferred until pinned.
 */
export interface StableLimitsSkuInput {
  fundingMethod: string;
  facetCorridorCode?: string | null;
  routeCorridorCode?: string | null;
  stickyCorridorCode?: string | null;
  payoutMethod?: string | null;
  payerCode?: string | null;
  customerFee?: number | null;
}

const firstNonEmpty = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
};

/**
 * Build the limits-resolve identity for a listed funding method.
 * Order: facet rail → route rail → sticky. Missing corridor → undefined (skip).
 */
export function stableLimitsSku(
  input: StableLimitsSkuInput
): FundingLimitsSku | undefined {
  const corridorCode = firstNonEmpty(
    input.facetCorridorCode,
    input.routeCorridorCode,
    input.stickyCorridorCode
  );
  if (!corridorCode) return undefined;
  return {
    corridorCode,
    fundingMethod: input.fundingMethod,
    payoutMethod: input.payoutMethod?.trim() || undefined,
    payerCode: input.payerCode?.trim() || undefined,
    customerFee: input.customerFee ?? null,
  };
}

export interface LimitsFacetBest {
  corridorCode: string;
}

/** Pre-quote `facets` slice used to harvest rails (D20). */
export interface LimitsFacets {
  fundingMethods?: Array<{ fundingMethod: string; best: LimitsFacetBest }>;
  payoutMethods?: Array<{ payoutMethod?: string; best: LimitsFacetBest }>;
  payers?: Array<{ payerCode?: string; best: LimitsFacetBest }>;
}

/** Facet `best.corridorCode` for a canonical funding method, if present. */
export function facetCorridorForMethod(
  facets: LimitsFacets | null | undefined,
  method: string
): string | undefined {
  const upper = method.toUpperCase();
  const row = facets?.fundingMethods?.find(
    (f) => f.fundingMethod.toUpperCase() === upper
  );
  return firstNonEmpty(row?.best.corridorCode);
}

const rememberCorridor = (
  found: Map<string, string>,
  raw?: string | null
): void => {
  const trimmed = raw?.trim();
  if (!trimmed) return;
  const key = trimmed.toUpperCase();
  if (!found.has(key)) found.set(key, trimmed);
};

/**
 * D20: distinct corridors that can still compose this funding method.
 * Union of matching options, the method's facet best, every facet rail
 * (covers discovery `vary=fundingMethod` collapse), and sticky.
 */
export function corridorsForFundingMethod(
  method: string,
  input: {
    options?: Array<{ fundingMethod?: string; corridorCode?: string | null }>;
    facets?: LimitsFacets | null;
    stickyCorridors?: Iterable<string> | null;
  }
): string[] {
  const found = new Map<string, string>();
  const upper = method.toUpperCase();

  for (const option of input.options ?? []) {
    if ((option.fundingMethod ?? "").toUpperCase() !== upper) continue;
    rememberCorridor(found, option.corridorCode);
  }
  rememberCorridor(found, facetCorridorForMethod(input.facets, method));
  for (const row of input.facets?.fundingMethods ?? []) {
    rememberCorridor(found, row.best?.corridorCode);
  }
  for (const row of input.facets?.payoutMethods ?? []) {
    rememberCorridor(found, row.best?.corridorCode);
  }
  for (const row of input.facets?.payers ?? []) {
    rememberCorridor(found, row.best?.corridorCode);
  }
  for (const sticky of input.stickyCorridors ?? []) {
    rememberCorridor(found, sticky);
  }

  return [...found.values()].sort((a, b) => a.localeCompare(b));
}

/** One resolve identity per harvested corridor (D20). */
export function skusForCorridors(
  fundingMethod: string,
  corridors: string[],
  extras?: {
    payoutMethod?: string | null;
    payerCode?: string | null;
    customerFee?: number | null;
  }
): FundingLimitsSku[] {
  const payoutMethod = extras?.payoutMethod?.trim() || undefined;
  const payerCode = extras?.payerCode?.trim() || undefined;
  const customerFee = extras?.customerFee ?? null;
  return corridors.map((corridorCode) => ({
    corridorCode,
    fundingMethod,
    payoutMethod,
    payerCode,
    customerFee,
  }));
}

const hasNumericSide = (bound?: FundingAmountBound): boolean =>
  bound != null && (bound.minAmount != null || bound.maxAmount != null);

/**
 * D21: intersection of bounded resolve responses. Unbounded / missing
 * responses do not participate and cannot wipe a bounded rail.
 */
export function mergeRemainingBounds(
  responses: Array<FundingAmountBound | null | undefined>
): FundingAmountBound {
  let minAmount: number | null = null;
  let maxAmount: number | null = null;
  let sawBounded = false;
  for (const bound of responses) {
    if (!bound) continue;
    const hasMin = bound.minAmount != null;
    const hasMax = bound.maxAmount != null;
    if (!hasMin && !hasMax) continue;
    sawBounded = true;
    if (hasMin) {
      minAmount =
        minAmount == null
          ? bound.minAmount
          : Math.max(minAmount, bound.minAmount as number);
    }
    if (hasMax) {
      maxAmount =
        maxAmount == null
          ? bound.maxAmount
          : Math.min(maxAmount, bound.maxAmount as number);
    }
  }
  if (!sawBounded) return { minAmount: null, maxAmount: null };
  return { minAmount, maxAmount };
}

/**
 * Keep bounds for methods still listed when this fetch skipped or failed.
 * Replace when `next` has an entry, except an unbounded `{null,null}` must
 * not wipe a still-numeric previous bound (D21).
 */
export function mergeListedBounds<K extends string>(
  prev: Partial<Record<K, FundingAmountBound>>,
  next: Partial<Record<K, FundingAmountBound>>,
  listed: readonly K[]
): Partial<Record<K, FundingAmountBound>> {
  const result: Partial<Record<K, FundingAmountBound>> = {};
  for (const key of listed) {
    if (Object.prototype.hasOwnProperty.call(next, key)) {
      const incoming = next[key];
      const previous = prev[key];
      if (
        incoming &&
        !hasNumericSide(incoming) &&
        previous &&
        hasNumericSide(previous)
      ) {
        result[key] = previous;
      } else {
        result[key] = incoming;
      }
    } else if (prev[key] !== undefined) {
      result[key] = prev[key];
    }
  }
  return result;
}

export type TranslateFundingLimit = (
  key: "payment.minMax" | "payment.minOnly" | "payment.maxOnly",
  options: { min?: string; max?: string }
) => string;

/** D17: paint whatever sides the engine returned. Empty only when both null. */
export function formatFundingLimitSubtitle(
  t: TranslateFundingLimit,
  minAmount: number | null | undefined,
  maxAmount: number | null | undefined,
  formatLimit: (value: number) => string
): string {
  const hasMin = minAmount != null;
  const hasMax = maxAmount != null;
  if (hasMin && hasMax) {
    return t("payment.minMax", {
      min: formatLimit(minAmount),
      max: formatLimit(maxAmount),
    });
  }
  if (hasMin) {
    return t("payment.minOnly", { min: formatLimit(minAmount) });
  }
  if (hasMax) {
    return t("payment.maxOnly", { max: formatLimit(maxAmount) });
  }
  return "";
}

/** Cache identity for listed methods + N query SKUs. Amount is never part of the key. */
export function fundingLimitsOptionsKey<T extends string>(
  methods: T[],
  queriesForMethod: (method: T) => FundingLimitsSku[] | FundingLimitsSku | undefined
): string {
  return methods
    .map((method) => {
      const raw = queriesForMethod(method);
      const queries = Array.isArray(raw) ? raw : raw ? [raw] : [];
      if (queries.length === 0) return method;
      const identities = queries
        .map((sku) =>
          [
            sku.fundingMethod || method,
            sku.corridorCode,
            sku.payerCode ?? "",
            sku.payoutMethod ?? "",
          ].join(":")
        )
        .sort()
        .join(",");
      return `${method}:${identities}`;
    })
    .join("|");
}

export type SendBoundVerdict = "ok" | "below_min" | "above_max" | "skip";

/**
 * Compare a send-leg amount against a resolved per-tx bound.
 * Missing min/max → skip (fail-open; server still enforces).
 */
export function compareSendToBound(
  sendAmount: number | null | undefined,
  bound: { minAmount: number | null; maxAmount: number | null } | undefined
): SendBoundVerdict {
  if (sendAmount == null || !Number.isFinite(sendAmount) || sendAmount <= 0) {
    return "skip";
  }
  if (!bound) return "skip";
  const { minAmount, maxAmount } = bound;
  if (minAmount == null && maxAmount == null) return "skip";
  if (minAmount != null && sendAmount < minAmount) return "below_min";
  if (maxAmount != null && sendAmount > maxAmount) return "above_max";
  return "ok";
}
